<template>
  <div class="pos-component">
    <!-- Header with navigation -->
    <div class="header-container mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <vue-feather class="nav-icon me-3" type="arrow-left-circle" @click="$router.go(-1)" stroke="#000000" size="35" stroke-width="1.5"></vue-feather>
          <h4 class="mb-0">Point of Sale</h4>
          <button 
            @click="refreshStockAndPrice" 
            :disabled="isRefreshing"
            class="btn btn-sm btn-outline-primary ms-3"
            title="Refresh products from server"
          >
            <vue-feather 
              :type="isRefreshing ? 'loader' : 'refresh-cw'" 
              size="16" 
              :class="{'rotating': isRefreshing}"
            ></vue-feather>
            <span class="ms-1 d-none d-md-inline">{{ isRefreshing ? 'Syncing...' : 'Sync' }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center">
          <span class="badge bg-primary me-3">{{ store.name }}</span>
          <vue-feather class="nav-icon" type="log-out" @click="logout" stroke="#fc0303" size="30" stroke-width="1.5"></vue-feather>
        </div>
      </div>
      <hr class="border-2 border-primary border-opacity-50 mt-3" />
    </div>

    <!-- Main content -->
    <div class="row g-4">
      <!-- Left column - Products and Cart -->
      <div class="col-xxl-8 col-lg-7">
        <!-- Product Search and List -->
        <div class="card shadow-sm mb-4">
          <div class="card-header bg-light">
            <h5 class="mb-0">Product Search</h5>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-4">
              <Spinner spinner-class="mb-1" />
              <p class="text-muted mt-2">Loading products...</p>
            </div>
            <div v-if="!loading" class="product-search-container">
              <div class="input-group mb-3">
                <span class="input-group-text bg-primary text-white">
                  <vue-feather type="search" size="20"></vue-feather>
                </span>
                <input
                    v-model="dataTableSearch"
                    autofocus
                    ref="prodSearch"
                    size="lg"
                    v-on:keyup.enter="onEnter"
                    placeholder="Search by name, SKU or scan barcode..."
                    class="form-control form-control-lg border-primary"
                />
              </div>

              <DataTable
                  show-filter-buttons
                  :data="productData"
                  :headers="dataTableHeaders"
                  :search-keyword="dataTableSearch"
                  :per-page="dataTablePerPage"
                  :current-page="dataTablePage"
                  v-if="dataTableShow"
                  class="product-table"
              >
                <template v-slot:default="{row}">
                  <Cell
                      v-for="(cellData, key) in row"
                      :class="row[10] === true ? 'bg-info-light' : ''"
                      class="text-nowrap product-cell"
                      @click="addCartProduct(row[0])"
                      :cell-classes="[
                      ['selected', 'status', 'reorder'].indexOf(key.toString().toLowerCase()) !== -1 ? 'd-flex justify-content-center' : '',
                    ]"
                  >
                    <span v-if="key === 0">{{cellData.split("-").pop()}}</span>
                    <span v-else>{{cellData}}</span>
                  </Cell>
                </template>
              </DataTable>
            </div>
          </div>
        </div>

        <!-- Shopping Cart -->
        <div class="card shadow-sm">
          <div class="card-header bg-light d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Items in Cart</h5>
            <button @click="clearCart" class="btn btn-sm btn-outline-danger" v-if="cartAvailable">
              <vue-feather type="trash-2" size="16" class="me-1"></vue-feather>
              Clear Cart
            </button>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                <tr>
                  <th>Image</th>
                  <th>Product</th>
                  <th>Price</th>
                  <th>Quantity</th>
                  <th>Total</th>
                  <th></th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="!cartAvailable">
                  <td colspan="5" class="text-center py-4">
                    <vue-feather type="shopping-cart" size="40" stroke="#ccc" class="mb-2"></vue-feather>
                    <p class="text-muted">No products in cart</p>
                    <p class="small text-muted">Search for products above to add them to your cart</p>
                  </td>
                </tr>
                <tr v-else v-for="cart in cartProducts" :key="cart.id" class="border-bottom">
                  <td>
                    <img
                        :src="cart.product.avatar ?? 'https://chrisdellasega.com/wp-content/uploads/2023/03/image-placeholder.jpg'"
                        width="60"
                        class="product-avatar rounded shadow-sm"
                        alt="Product Image"
                    />
                  </td>
                  <td>
                    <div class="d-flex flex-column">
                      <span class="fw-bold">{{ cart.product.name }}</span>
                      <span class="text-muted small font-monospace">{{ cart.product.sku }}</span>
                      <small v-if="cart.product.description" class="text-muted d-none d-md-block">{{ cart.product.description }}</small>
                    </div>
                  </td>
                  <td>
                    <span class="price-tag">{{ formatNumber(cart.price, false) }}</span>
                  </td>
                  <td>
                    <div class="input-group input-group-sm quantity-control">
                      <button
                          class="btn btn-outline-secondary"
                          :disabled="cart.quantity <= 1"
                          @click="quantityButtons(cart.product_id, 'minus')"
                      >
                        <i class="bi bi-dash"></i>
                      </button>
                      <input
                          :ref="cart.product_id"
                          type="text"
                          @focus="savePreviousQuantity(cart.product_id, cart.quantity)"
                          @change="handleQuantityInput(cart.product_id, $event.target.value)"
                          class="form-control text-center"
                          :value="cart.quantity"
                      >
                      <button
                          class="btn btn-outline-secondary"
                          @click="quantityButtons(cart.product_id, 'plus')"
                      >
                        <i class="bi bi-plus"></i>
                      </button>
                    </div>
                  </td>
                  <td>
                    <div>
                      <span class="price-tag">{{ formatNumber(cart.price * cart.quantity, false) }}</span>
                      <span class="badge bg-light text-dark ms-1">{{ cart.product.tax_category.label }}</span>
                    </div>
                  </td>
                  <td>
                    <button
                        class="btn btn-sm btn-outline-danger"
                        @click="removeCartProduct(cart.product_id)"
                    >
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
                <tfoot v-if="cartAvailable" class="table-light">
                <tr>
                  <td colspan="6" class="text-end fw-bold h5 py-3">
                    <span style="font-size: 1.8rem">{{ formatNumber(calculateTotal) }}</span>
                  </td>
                </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="card shadow mb-3 mt-3" v-if="cartAvailable">
          <div class="card-header bg-light">
            <h5 class="mb-0">Quick Actions</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-6 col-sm-3" v-print="'#printMe'">
                <div class="quick-action-btn bg-warning">
                  <vue-feather type="printer" size="24" class="mb-2"></vue-feather>
                  <span>Print Receipt</span>
                </div>
              </div>
              <div class="col-6 col-sm-3" @click="getCompleteStk">
                <div class="quick-action-btn bg-dark-subtle">
                  <vue-feather type="credit-card" size="24" class="mb-2"></vue-feather>
                  <span>Check STK</span>
                </div>
              </div>
              <div class="col-6 col-sm-3" :class="{'disabled opacity-50': !canCompleteSale}" @click="openCheckTillModal">
                <div class="quick-action-btn bg-info-subtle">
                  <vue-feather type="dollar-sign" size="24" class="mb-2"></vue-feather>
                  <span>Check TILL</span>
                </div>
              </div>
              <div class="col-6 col-sm-3" @click="$router.push({path: 'dashboard'})">
                <div class="quick-action-btn bg-success-subtle">
                  <vue-feather type="home" size="24" class="mb-2"></vue-feather>
                  <span>Dashboard</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right column - Payment and Actions -->
      <div class="col-xxl-4 col-lg-5">
        <!-- Payment Summary -->
        <div class="card shadow mb-4">
          <div class="card-body">
            <div class="sale-reference mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <span class="text-muted">Sale Reference:</span>
                <span class="fw-bold">{{ saleReference }}</span>
              </div>
            </div>

            <div class="discount-section mb-3">
              <label class="form-label fw-bold">Discount Amount</label>
              <div class="input-group">
                <span class="input-group-text">Ksh</span>
                <input type="number" class="form-control" v-model="discount" min="0" placeholder="Enter discount amount">
              </div>
            </div>

            <div class="payment-method-section mb-3">
              <label class="form-label fw-bold">Payment Method</label>
              <div class="d-flex gap-2">
                <div class="form-check form-check-inline payment-option flex-grow-1">
                  <input class="form-check-input" type="radio" id="cash" v-model="transaction.method" value="cash">
                  <label class="form-check-label w-100 text-center py-2" for="cash">
                    <vue-feather type="dollar-sign" size="20" class="me-1"></vue-feather>
                    CASH
                  </label>
                </div>
                <div class="form-check form-check-inline payment-option flex-grow-1">
                  <input class="form-check-input" type="radio" id="mpesa" v-model="transaction.method" value="mpesa">
                  <label class="form-check-label w-100 text-center py-2" for="mpesa">
                    <vue-feather type="smartphone" size="20" class="me-1"></vue-feather>
                    MPESA
                  </label>
                </div>
                <div class="form-check form-check-inline payment-option flex-grow-1">
                  <input class="form-check-input" type="radio" id="pdq" v-model="transaction.method" value="pdq">
                  <label class="form-check-label w-100 text-center py-2" for="pdq">
                    <vue-feather type="credit-card" size="20" class="me-1"></vue-feather>
                    PDQ
                  </label>
                </div>
              </div>
            </div>

            <div class="payment-details mb-4">
              <div v-if="transaction.method === 'cash'">
                <label class="form-label fw-bold">Amount Paid</label>
                <div class="input-group">
                  <span class="input-group-text">Ksh</span>
                  <input
                      type="number"
                      class="form-control"
                      v-model="transaction.paid"
                      :min="calculateTotal - discount"
                      placeholder="Enter amount received"
                  >
                </div>
              </div>
              <div v-if="transaction.method === 'mpesa'" class="text-center">
                <button
                    :disabled="!cartAvailable"
                    class="btn btn-success w-100 py-2"
                    @click="stkModal = true;"
                >
                  <vue-feather type="send" size="18" class="me-1"></vue-feather>
                  Send STK Push
                </button>
              </div>
              <div v-if="transaction.method === 'pdq'" class="text-center">
                <button
                    :disabled="!cartAvailable || pdqProcessing"
                    class="btn btn-primary w-100 py-2"
                    @click="initiatePDQPayment"
                >
                  <vue-feather :type="pdqProcessing ? 'loader' : 'credit-card'" size="18" class="me-1" :class="{'rotating': pdqProcessing}"></vue-feather>
                  {{ pdqProcessing ? 'Processing...' : 'Process Card Payment' }}
                </button>
              </div>
            </div>

            <div class="summary-section p-3 bg-light rounded mb-3">
              <div class="d-flex justify-content-between mb-2">
                <span>Subtotal:</span>
                <span>{{ formatNumber(calculateTotal - calculateTax) }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span>Tax:</span>
                <span>{{ formatNumber(calculateTax) }}</span>
              </div>
              <div v-if="discount > 0" class="d-flex justify-content-between mb-2">
                <span>Discount:</span>
                <span>-{{ formatNumber(discount) }}</span>
              </div>
              <div v-if="transaction.method === 'cash'" class="d-flex justify-content-between mb-2">
                <span>Change:</span>
                <span>{{ formatNumber(transaction.paid - (calculateTotal - discount)) }}</span>
              </div>
              <div class="d-flex justify-content-between pt-2 border-top mt-2">
                <span class="fw-bold">Total:</span>
                <span class="fw-bold price-tag-lg">{{ formatNumber(calculateTotal - discount) }}</span>
              </div>
            </div>

            <button
                :disabled="!canCompleteSale"
                class="btn btn-primary w-100 py-3 fw-bold"
                @click="validate"
            >
              <vue-feather type="check-circle" size="20" class="me-1"></vue-feather>
              COMPLETE SALE
            </button>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="card shadow mb-3">
          <div class="card-header bg-light">
            <h5 class="mb-0">Quick Actions</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-6" v-print="'#printMe'">
                <div class="quick-action-btn bg-warning">
                  <vue-feather type="printer" size="24" class="mb-2"></vue-feather>
                  <span>Print Receipt</span>
                </div>
              </div>
              <div class="col-6" @click="getCompleteStk">
                <div class="quick-action-btn bg-dark-subtle">
                  <vue-feather type="credit-card" size="24" class="mb-2"></vue-feather>
                  <span>Check STK</span>
                </div>
              </div>
              <div class="col-6" :class="{'disabled opacity-50': !canCompleteSale}" @click="openCheckTillModal">
                <div class="quick-action-btn bg-info-subtle">
                  <vue-feather type="dollar-sign" size="24" class="mb-2"></vue-feather>
                  <span>Check TILL</span>
                </div>
              </div>
              <div class="col-6" @click="$router.push({path: 'dashboard'})">
                <div class="quick-action-btn bg-success-subtle">
                  <vue-feather type="home" size="24" class="mb-2"></vue-feather>
                  <span>Dashboard</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- STK Modal -->
    <Modal :open="stkModal" @closed="stkModal = false" dialog-size="modal-md">
      <div class="modal-header bg-primary text-white">
        <h5 class="modal-title m-0">MPESA Payment Request</h5>
        <button @click="stkModal = false" type="button" class="btn-close btn-close-white" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body p-4">
        <div class="text-center mb-4">
          <vue-feather type="smartphone" size="48" stroke="#28a745" stroke-width="1"></vue-feather>
          <h6 class="mt-2">Enter the phone number to receive the payment request</h6>
        </div>
        <form @submit.prevent="">
          <div class="form-group">
            <label for="phone" class="form-label fw-bold">MPESA Number:</label>
            <div class="input-group mb-3">
              <span class="input-group-text"><i class="bi bi-phone"></i></span>
              <input
                  type="text"
                  id="phone"
                  class="form-control form-control-lg text-center"
                  placeholder="0711......"
                  v-model="phone"
                  style="letter-spacing: 0.3em; font-size: 24px"
              >
            </div>
            <div class="form-text">
              The customer will receive an STK push notification to authorize payment of
              <strong>{{ formatNumber(calculateTotal - discount) }}</strong>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="stkModal = false">
          <i class="bi bi-x-lg me-1"></i> Cancel
        </button>
        <button type="button" class="btn btn-success" @click="stkRequest">
          <i class="bi bi-send me-1"></i> Send Request
        </button>
      </div>
    </Modal>
    <!-- STK Modal End -->

    <!-- C2B Transactions Modal -->
    <Modal :open="c2bModal" @closed="closeC2bModal" dialog-size="modal-lg">
      <div class="modal-header bg-success text-white">
        <h5 class="modal-title m-0">C2B Transactions</h5>
        <button @click="closeC2bModal" type="button" class="btn-close btn-close-white" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body p-4">
        <div v-if="c2bLoading" class="text-center py-4">
          <vue-feather type="loader" size="48" stroke="#28a745" stroke-width="1" class="rotating"></vue-feather>
          <h6 class="mt-3">Checking for transactions...</h6>
          <p class="text-muted">Polling every 2 seconds</p>
        </div>

        <div v-if="!c2bLoading && c2bTransactions.length === 0" class="text-center py-4">
          <vue-feather type="inbox" size="48" stroke="#ccc" stroke-width="1"></vue-feather>
          <h6 class="mt-3 text-muted">No transactions found</h6>
          <p class="text-muted">Waiting for C2B payments...</p>
        </div>

        <div v-if="c2bTransactions.length > 0" class="transactions-list">
          <div class="alert alert-success">
            <i class="bi bi-check-circle me-2"></i>
            Found {{ c2bTransactions.length }} transaction(s)
          </div>

          <div v-for="transaction in c2bTransactions" :key="transaction.id" class="card mb-3 border">
            <div class="card-body">
              <div class="row h4">
                <div class="col-md-6">
                  <div class="d-flex mb-2">
                    <span class="text-muted">Name:</span>
                    <span class="fw-bold ms-3">{{ transaction.first_name }}</span>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="d-flex mb-2">
                    <span class="text-muted">Amount:</span>
                    <span class="fw-bold ms-3">{{ formatNumber(transaction.trans_amount) }}</span>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-3 pt-3 border-top h5">
                <div>
                  <small class="text-muted">Trans ID: {{ transaction.trans_id }}</small>
                </div>
                <button
                  class="btn btn-primary btn-md d-block"
                  @click="assignC2BTransaction(transaction)"
                >
                  <i class="bi bi-check-circle me-1"></i> Assign
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" @click="closeC2bModal">
          <i class="bi bi-x-lg me-1"></i> Close
        </button>
      </div>
    </Modal>
    <!-- C2B Transactions Modal End -->

    <!-- Payment Modal -->
    <Modal :open="paymentModal" @closed="paymentModal = false" dialog-size="modal-md">
      <div class="modal-header bg-success text-white">
        <h5 class="modal-title m-0">Payment Confirmation</h5>
        <button @click="paymentModal = false" type="button" class="btn-close btn-close-white" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body p-4">
        <div class="text-center mb-3">
          <vue-feather type="check-circle" size="48" stroke="#28a745" stroke-width="1"></vue-feather>
          <h5 class="mt-2">Payment Received</h5>
        </div>

        <div class="card bg-light">
          <div class="card-body">
            <div class="d-flex justify-content-between mb-2">
              <span class="text-muted">Reference:</span>
              <span class="fw-bold">{{ saleReference }}</span>
            </div>
            <div class="d-flex justify-content-between mb-2">
              <span class="text-muted">{{ transaction.method === 'pdq' ? 'Card Number:' : 'Phone Number:' }}</span>
              <span class="fw-bold">{{ payment.phone }}</span>
            </div>
            <div class="d-flex justify-content-between mb-2">
              <span class="text-muted">Amount:</span>
              <span class="fw-bold">{{ formatNumber(payment.amount) }}</span>
            </div>
            <div class="d-flex justify-content-between mb-2">
              <span class="text-muted">{{ transaction.method === 'pdq' ? 'Voucher No:' : 'MPESA Code:' }}</span>
              <span class="fw-bold">{{ payment.code }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-primary btn-lg w-100" data-dismiss="modal" @click="paymentModal = false; makeSale()">
          <i class="bi bi-check-lg me-1"></i> COMPLETE SALE
        </button>
      </div>
    </Modal>
    <!-- Payment Modal End -->

    <!-- Receipt -->
    <div v-if="lastSale !== null" class="d-none">
      <div id="printMe">
        <Receipt :sale="lastSale.sale" :products="lastSale.details" :organization="organization"></Receipt>
      </div>
    </div>
    <!-- Receipt End -->

    <!-- Start Shift Modal -->
    <Modal :open="startShiftModal" @closed="preventClose" dialog-size="modal-md" :static-backdrop="true">
      <div class="modal-header bg-primary text-white">
        <h5 class="modal-title m-0">
          <i class="bi bi-play-circle me-2"></i>Start Your Shift
        </h5>
      </div>
      <div class="modal-body">
        <div class="alert alert-info mb-3">
          <i class="bi bi-info-circle me-2"></i>
          You need to start a shift before making sales.
        </div>
        <form @submit.prevent="startShift">
          <div class="row">
            <div class="form-group col-md-12 mb-3">
              <label for="opening_cash" class="form-label fw-medium mb-2">Opening Cash Amount:</label>
              <input type="number" step="0.01" class="form-control" v-model="newShift.opening_cash" required @focus="$event.target.select()">
            </div>
            <div class="form-group col-md-12 mb-3">
              <label for="notes" class="form-label fw-medium mb-2">Notes (Optional):</label>
              <textarea class="form-control" v-model="newShift.notes" rows="3" @focus="stopPropagation"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="submit" class="btn btn-primary w-100">
              <i class="bi bi-play-circle me-1"></i> Start Shift
            </button>
          </div>
        </form>
      </div>
    </Modal>
    <!-- Start Shift Modal End -->

    <!-- Cart Authorization OTP Modal -->
    <Modal :open="cartOtpModal" @closed="cancelCartOtp" dialog-size="modal-md" :static-backdrop="true">
      <div class="modal-header bg-warning-subtle text-dark border-bottom">
        <h5 class="modal-title m-0 fw-bold">
          <i class="bi bi-shield-lock me-2 text-warning"></i>Authorization Required
        </h5>
        <button type="button" class="btn-close" @click="cancelCartOtp"></button>
      </div>
      <div class="modal-body p-4">

        <div class="card bg-light border-0 mb-3" v-if="pendingCartAction">
          <div class="card-body py-2 px-3">
            <span class="text-muted small text-uppercase fw-semibold d-block">Requested Action:</span>
            <div class="d-flex justify-content-between align-items-center mt-1">
              <span class="fw-bold">{{ pendingCartAction.description }}</span>
              <span class="badge bg-danger" v-if="pendingCartAction.type === 'remove_product' || pendingCartAction.type === 'clear_cart'">Delete</span>
              <span class="badge bg-warning text-dark" v-else>Qty: {{ pendingCartAction.oldQuantity }} &rarr; {{ pendingCartAction.quantity }}</span>
            </div>
            <div class="small text-muted mt-1" v-if="pendingCartAction.productName">
              Item: <strong>{{ pendingCartAction.productName }}</strong>
            </div>
          </div>
        </div>

        <div class="text-center my-4">
          <label class="form-label fw-bold mb-2">Enter 4-Digit Authorization OTP:</label>
          <div class="d-flex justify-content-center">
            <input
              ref="cartOtpInput"
              type="text"
              maxlength="4"
              class="form-control form-control-lg text-center font-monospace fw-bold fs-2"
              style="letter-spacing: 0.5rem; max-width: 200px;"
              placeholder="••••"
              v-model="cartOtpCode"
              @keyup.enter="verifyCartOtp"
              @focus="stopPropagation"
              autofocus
            />
          </div>
          <div v-if="cartOtpError" class="text-danger small mt-2">
            <i class="bi bi-x-circle me-1"></i>{{ cartOtpError }}
          </div>
          <div v-if="devOtpHint" class="text-info small mt-2 font-monospace">
            [{{ devOtpHint }}]
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center pt-2">
          <button
            type="button"
            class="btn btn-sm btn-link text-decoration-none text-muted p-0"
            :disabled="cartOtpLoading || otpCountdown > 0"
            @click="requestCartOtp"
          >
            <i class="bi bi-arrow-clockwise me-1"></i>
            <span v-if="otpCountdown > 0">Resend OTP in {{ otpCountdown }}s</span>
            <span v-else>Resend OTP</span>
          </button>
          <span v-if="cartOtpLoading" class="spinner-border spinner-border-sm text-primary" role="status"></span>
        </div>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" @click="cancelCartOtp">
          <i class="bi bi-x-lg me-1"></i> Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary px-4"
          :disabled="cartOtpVerifying || !cartOtpCode || cartOtpCode.length !== 4"
          @click="verifyCartOtp"
        >
          <span v-if="cartOtpVerifying" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-check-lg me-1"></i> Authorize & Perform
        </button>
      </div>
    </Modal>
    <!-- Cart Authorization OTP Modal End -->

  </div>
</template>

<script>
import {decrypt, encrypt} from "@/utils/crypto";
import formatDate from "@/utils/formatDate";
import formatNumber from "@/utils/formatNumber";
import confirmDialog from "@/utils/confirmDialog";
import posService from "@/api/posService";
import saleService from "@/api/saleService";
import authService from "@/api/authService";
import storeMpesaService from "@/api/storeMpesaService";
import pdqService from "@/api/pdqService";
import shiftService from "@/api/shiftService";
import notify from "@/utils/notify";
import print from 'vue3-print-nb';
import Receipt from "@/components/receipt/Receipt.vue";

export default {
  name: "StockTakeDetails",
  components: {Receipt},
  directives: {
    print
  },

  data(){
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      profile: {},
      store: {},
      organization: {},
      discount: 0,
      transaction: {
        organization_id: this.organization.id,
        store_id: null,
        reference: '',
        type: 'sale',
        status: 'pending',
        amount: 0,
        paid: 0,
        balance: 0,
        method: 'cash',
        payment_details: {},
      },
      cartProducts: [],
      dataTableShow: false,
      selectedProduct: {
        product: {},
      },
      loading: true,
      dataTableHeaders: [
        '#',
        'SKU',
        'Code',
        'Name',
        'Price'
      ],
      dataTable: [],
      dataTableSearch: '',
      dataTablePage: 1,
      dataTablePerPage: 5,
      subscription: {
        package: {}
      },
      stkModal: false,
      phone: '',
      saleReference: 'generating...',
      paymentModal: false,
      paymentSuccess: false,
      payment: {},
      c2bModal: false,
      c2bLoading: false,
      c2bTransactions: [],
      c2bPollingInterval: null,
      pdqProcessing: false,
      isRefreshing: false,
      pdqInitialized: false,
      pdqTerminalInfo: null,
      pdqBankId: 'KCB_BANK',
      lastSale: {
        sale: {
          user: {},
          store: {},
          transactions: [],
        },
        details: []
      },
      startShiftModal: false,
      activeShift: null,
      newShift: {
        opening_cash: 0,
        notes: ''
      },
      cartQuantities: {},
      cartOtpModal: false,
      cartOtpCode: '',
      cartOtpLoading: false,
      cartOtpVerifying: false,
      cartOtpError: '',
      devOtpHint: '',
      otpAuthorizedEmails: '',
      otpCountdown: 0,
      otpInterval: null,
      pendingCartAction: null
    }
  },

  methods: {
    formatDate,
    formatNumber,
    confirmDialog,
    async checkActiveShift() {
      const cachedShift = sessionStorage.getItem('active_shift')
      if (cachedShift) {
        this.activeShift = JSON.parse(decrypt(cachedShift))
        this.startShiftModal = false
        return
      }

      this.activeShift = await shiftService.getActiveShift()
      if (this.activeShift) {
        sessionStorage.setItem('active_shift', encrypt(JSON.stringify(this.activeShift)))
        this.startShiftModal = false
      } else {
        this.startShiftModal = true
      }
    },
    preventClose() {
      return false
    },
    stopPropagation(e) {
      e.stopPropagation()
    },
    async startShift() {
      const shiftData = {
        store_id: this.store.id,
        opening_cash: this.newShift.opening_cash,
        notes: this.newShift.notes
      }
      const shift = await shiftService.startShift(shiftData)
      if (shift) {
        this.activeShift = shift
        sessionStorage.setItem('active_shift', encrypt(JSON.stringify(shift)))
        this.startShiftModal = false
        this.newShift = {
          opening_cash: 0,
          notes: ''
        }
      }
    },
    async getStoreProducts() {
      this.dataTable = await posService.getPOSProducts(this.store.id)
      this.loading = false
    },
    async getLastSale() {
      const reference = sessionStorage.getItem('lastSaleReference') || null
      this.lastSale = await posService.getLastSale(reference)
    },
    onEnter(e) {
      let entry = e.target.value
      let data = this.dataTable.filter(
          item => item.product.sku.indexOf(entry) > -1
      )
      if (data.length === 1) {
        this.addCartProduct(data[0].product_id)
      } else {
        notify.alert.error('Product not found', 3000)
        e.target.value = ''
      }
    },
    addCartProduct(productId) {
      const cachedShift = sessionStorage.getItem('active_shift')
      if (!cachedShift) {
        notify.alert.error('No active shift found', 3000)
        return
      }
      this.selectedProduct = this.getSelectedProduct(productId)
      let cart = sessionStorage.getItem('cart')
      if (this.selectedProduct.stock > 0) {
        if (cart === null) {
          this.selectedProduct.quantity = 1
          sessionStorage.setItem('cart', JSON.stringify([this.selectedProduct]))
        } else {
          let data = JSON.parse(cart)
          let cartItems = data.filter(
              item => item.product_id.indexOf(productId) > -1
          )
          if (cartItems.length === 1) {
            if (this.selectedProduct.stock < cartItems[0].quantity + 1) {
              notify.alert.error('Product out of stock', 3000)
              return
            } else {
              cartItems[0].quantity++
            }
          } else {
            this.selectedProduct.quantity = 1
            data.push(this.selectedProduct)
          }
          sessionStorage.setItem('cart', JSON.stringify(data))
        }
      } else {
        notify.alert.error('Product out of stock', 3000)
      }
      this.getCartProducts()
    },
    savePreviousQuantity(productId, quantity) {
      this.cartQuantities[productId] = quantity;
    },
    handleQuantityInput(productId, val) {
      const newQty = parseInt(val);
      const oldQty = this.cartQuantities[productId] || 1;
      const cartItem = this.cartProducts.find(item => item.product_id === productId);

      if (isNaN(newQty) || newQty < 1) {
        if (this.profile.role !== 'primary') {
          if (cartItem) cartItem.quantity = oldQty;
          if (this.$refs[productId] && this.$refs[productId][0]) {
            this.$refs[productId][0].value = oldQty;
          }
          this.initiateCartOtpAuthorization({
            type: 'remove_product',
            productId: productId,
            productName: cartItem?.product?.name || 'Item',
            description: `Remove '${cartItem?.product?.name || 'item'}' from cart`
          });
          return;
        } else {
          this.removeCartProduct(productId);
          return;
        }
      }

      if (newQty < oldQty) {
        if (this.profile.role !== 'primary') {
          if (cartItem) cartItem.quantity = oldQty;
          if (this.$refs[productId] && this.$refs[productId][0]) {
            this.$refs[productId][0].value = oldQty;
          }
          this.initiateCartOtpAuthorization({
            type: 'update_quantity',
            productId: productId,
            quantity: newQty,
            oldQuantity: oldQty,
            productName: cartItem?.product?.name || 'Item',
            description: `Reduce quantity of '${cartItem?.product?.name || 'item'}'`
          });
          return;
        }
      }

      this.cartQuantities[productId] = newQty;
      this.updateCartProduct(productId, newQty, newQty > oldQty ? 'plus' : 'minus');
    },
    updateCartProduct(productId, quantity, type) {
      if (quantity >= 1) {
        let data = JSON.parse(sessionStorage.getItem('cart'))
        data = data.map(item => {
          if (item.product_id === productId) {
            if (item.stock < item.quantity + quantity - 1 && type === 'plus') {
              notify.alert.error('Product out of stock', 3000)
            } else {
              item.quantity = quantity
            }
          }
          return item
        })
        sessionStorage.setItem('cart', JSON.stringify(data))
        this.getCartProducts()
      } else {
        this.removeCartProduct(productId)
      }
      this.$refs.prodSearch.focus()
    },
    removeCartProduct(productId) {
      const cartItem = this.cartProducts.find(item => item.product_id === productId);
      if (this.profile.role !== 'primary') {
        this.initiateCartOtpAuthorization({
          type: 'remove_product',
          productId: productId,
          productName: cartItem?.product?.name || 'Item',
          description: `Remove '${cartItem?.product?.name || 'item'}' from cart`
        });
        return;
      }
      this.doRemoveCartProduct(productId);
    },
    doRemoveCartProduct(productId) {
      let data = JSON.parse(sessionStorage.getItem('cart'))
      data = data.filter(
          item => item.product_id.indexOf(productId) === -1
      )
      sessionStorage.setItem('cart', JSON.stringify(data))
      delete this.cartQuantities[productId];
      this.getCartProducts()
    },
    getSelectedProduct(id) {
      let data = this.dataTable.filter(
          item => item.product_id.indexOf(id) > -1
      )
      return data[0]
    },
    getCartProducts() {
      let cart = sessionStorage.getItem('cart')
      if (cart === null) {
        sessionStorage.setItem('cart', JSON.stringify([]))
        this.cartProducts = []
      } else {
        this.cartProducts = JSON.parse(cart)
      }
      this.cartProducts.forEach(item => {
        this.cartQuantities[item.product_id] = item.quantity;
      });
      this.dataTableSearch = ''
    },
    quantityButtons(id, type) {
      let element = this.$refs[id][0]
      let currentVal = parseInt(element.value)
      if (isNaN(currentVal)) {
        currentVal = 1
      }
      if (type === 'minus') {
        if (currentVal <= 1) return;
        const cartItem = this.cartProducts.find(item => item.product_id === id);
        if (this.profile.role !== 'primary') {
          this.initiateCartOtpAuthorization({
            type: 'update_quantity',
            productId: id,
            quantity: currentVal - 1,
            oldQuantity: currentVal,
            productName: cartItem?.product?.name || 'Item',
            description: `Reduce quantity of '${cartItem?.product?.name || 'item'}'`
          });
          return;
        }
        element.value = currentVal - 1;
        this.cartQuantities[id] = currentVal - 1;
        this.updateCartProduct(id, currentVal - 1, 'minus');
      } else if (type === 'plus') {
        element.value = currentVal + 1;
        this.cartQuantities[id] = currentVal + 1;
        this.updateCartProduct(id, currentVal + 1, 'plus');
      }
    },
    initiateCartOtpAuthorization(action) {
      this.pendingCartAction = action;
      this.cartOtpModal = true;
      this.cartOtpCode = '';
      this.cartOtpError = '';
      this.devOtpHint = '';
      this.otpAuthorizedEmails = '';
      this.requestCartOtp();
    },
    async requestCartOtp() {
      this.cartOtpLoading = true;
      this.cartOtpError = '';
      try {
        const res = await posService.requestCartOtp(this.store.id, {
          action: this.pendingCartAction?.description || 'Cart action',
          product_name: this.pendingCartAction?.productName || 'Product',
        });
        if (res) {
          this.otpAuthorizedEmails = (res.authorized_users || []).map(u => `${u.name} (${u.email})`).join(', ');
          if (res.dev_otp) {
            this.devOtpHint = `Dev OTP: ${res.dev_otp}`;
          }
          notify.alert.success(res.message || 'OTP sent to authorized users', 3000);
          this.startOtpCountdown();
        }
      } catch (error) {
        this.cartOtpError = error?.response?.data?.message || 'Failed to request authorization OTP';
        notify.alert.error(this.cartOtpError, 4000);
      } finally {
        this.cartOtpLoading = false;
        this.$nextTick(() => {
          if (this.$refs.cartOtpInput) {
            this.$refs.cartOtpInput.focus();
          }
        });
      }
    },
    async verifyCartOtp() {
      if (!this.cartOtpCode || this.cartOtpCode.length !== 4) {
        this.cartOtpError = 'Please enter a 4-digit OTP';
        return;
      }
      this.cartOtpVerifying = true;
      this.cartOtpError = '';
      try {
        const res = await posService.verifyCartOtp(this.store.id, {
          otp: this.cartOtpCode,
          action: this.pendingCartAction?.type,
        });
        if (res && res.authorized) {
          notify.alert.success('Action authorized successfully', 3000);
          this.executePendingAction();
          this.cartOtpModal = false;
          this.cartOtpCode = '';
          this.stopOtpCountdown();
        }
      } catch (error) {
        this.cartOtpError = error?.response?.data?.message || 'Invalid or expired OTP';
        notify.alert.error(this.cartOtpError, 3000);
      } finally {
        this.cartOtpVerifying = false;
      }
    },
    executePendingAction() {
      if (!this.pendingCartAction) return;
      const action = this.pendingCartAction;
      if (action.type === 'update_quantity') {
        this.cartQuantities[action.productId] = action.quantity;
        const cartItem = this.cartProducts.find(item => item.product_id === action.productId);
        if (cartItem) cartItem.quantity = action.quantity;
        if (this.$refs[action.productId] && this.$refs[action.productId][0]) {
          this.$refs[action.productId][0].value = action.quantity;
        }
        this.updateCartProduct(action.productId, action.quantity, 'minus');
      } else if (action.type === 'remove_product') {
        this.doRemoveCartProduct(action.productId);
      } else if (action.type === 'clear_cart') {
        this.doClearCart();
      }
      this.pendingCartAction = null;
    },
    cancelCartOtp() {
      this.cartOtpModal = false;
      this.pendingCartAction = null;
      this.cartOtpCode = '';
      this.cartOtpError = '';
      this.stopOtpCountdown();
      this.getCartProducts();
    },
    startOtpCountdown() {
      this.otpCountdown = 60;
      if (this.otpInterval) clearInterval(this.otpInterval);
      this.otpInterval = setInterval(() => {
        if (this.otpCountdown > 0) {
          this.otpCountdown--;
        } else {
          this.stopOtpCountdown();
        }
      }, 1000);
    },
    stopOtpCountdown() {
      if (this.otpInterval) {
        clearInterval(this.otpInterval);
        this.otpInterval = null;
      }
    },
    validate() {
      confirmDialog('Make Sale', this.makeSale, 'Are you sure you want to make this sale')
    },
    async makeSale() {
      let products = []
      this.cartProducts.forEach( item => {
        let total = item.price * item.quantity
        let product = {
          product_id: item.product_id,
          quantity: item.quantity,
          tax: item.product.tax_category.rate/100 * total,
          price: item.price,
          buy: item.product.buy
        }
        products.push(product)
      })

      // Calculate total and update transaction
      let total = this.calculateTotal - this.discount
      this.transaction.amount = total
      this.transaction.paid = this.transaction.method === 'cash' ? this.transaction.paid : total
      this.transaction.balance = this.transaction.amount - this.transaction.paid
      this.transaction.status = this.transaction.balance <= 0 ? 'complete' : 'pending'

      // Prepare data for API
      let allData = {
        organization_id: this.store.organization_id,
        store_id: this.store.id,
        user_id: this.profile.id,
        reference: this.saleReference,
        discount: this.discount,
        source: 'pos',
        tax: this.calculateTax,
        total: total,
        paid: this.transaction.paid,
        products: products,
        transaction: this.transaction
      }
      let response = await saleService.createSale(allData)
      if (response) {
        // Save the reference for the sale we just completed
        sessionStorage.setItem('lastSaleReference', this.saleReference)

        sessionStorage.removeItem('cart')
        sessionStorage.removeItem('saleReference')
        
        // Reset state without full page reload
        this.cartProducts = []
        this.discount = 0
        this.transaction.paid = 0
        this.transaction.method = 'cash'
        this.transaction.payment_details = {}
        this.paymentSuccess = true
        this.paymentModal = false
        this.dataTableSearch = ''
        
        // Get fresh sale reference
        await this.getSaleReference()
        this.transaction.reference = this.saleReference
        
        // Update stock and price from backend
        await this.refreshStockAndPrice()

        // Get last sale for receipt using the completed sale's reference
        await this.getLastSale()
        
        // Auto-print receipt
        this.$nextTick(() => {
          this.autoPrintReceipt()
        })

        // Focus search
        this.focusSearch()
        
        notify.alert.success('Sale completed successfully!', 3000)
      }
    },
    autoPrintReceipt() {
      try {
        const printElement = document.getElementById('printMe')
        if (printElement) {
          const printWindow = window.open('', '', 'height=600,width=800')
          printWindow.document.write('<html><head><title>Receipt</title>')
          printWindow.document.write('<style>')
          printWindow.document.write(`
            @page {
              size: 2.8in 11in;
              margin-top: 0px;
              margin-left: -30px;
              margin-right: -30px;
            }
            body { font-family: Arial, sans-serif; margin: 2px; }
            table { width: 100%; }
            tr { width: 100%; }
            h1 { text-align: center; vertical-align: middle; }
            #logo {
              width: 60%;
              text-align: center;
              align-content: center;
              padding: 5px;
              margin: 2px;
              display: block;
              margin: 0 auto;
            }
            header {
              width: 100%;
              text-align: center;
              align-content: center;
              vertical-align: middle;
            }
            .items thead { text-align: center; }
            .center-align { text-align: center; }
            .bill-details td { font-size: 13px; }
            .receipt { font-size: medium; }
            .items .heading {
              font-size: 13px;
              text-transform: uppercase;
              border-top: 1px solid black;
              margin-bottom: 4px;
              border-bottom: 1px solid black;
              vertical-align: middle;
            }
            .items thead tr th:first-child,
            .items tbody tr td:first-child {
              width: 47%;
              min-width: 47%;
              max-width: 47%;
              word-break: break-all;
              text-align: left;
            }
            .items td {
              font-size: 14px;
              text-align: right;
              vertical-align: bottom;
            }
            .price::before { font-family: Arial; text-align: right; }
            .sum-up { text-align: right !important; }
            .total {
              font-size: 13px;
              border-top: 1px dashed black !important;
              border-bottom: 1px dashed black !important;
            }
            .total.text, .total.price { text-align: right; }
            .line { border-top: 1px solid black !important; }
            .dash-line-top {
              font-size: 13px;
              border-top: 1px dashed black !important;
            }
            .dash-line-bottom {
              font-size: 13px;
              border-bottom: 1px dashed black !important;
            }
            .heading.rate { width: 20%; }
            .heading.amount { width: 25%; }
            .heading.qty { width: 5%; }
            p { padding: 1px; margin: 0; }
            section, footer { font-size: 13px; text-align: center; }
          `)
          printWindow.document.write('</style></head><body>')
          printWindow.document.write(printElement.innerHTML)
          printWindow.document.write('</body></html>')
          printWindow.document.close()
          printWindow.focus()
          setTimeout(() => {
            printWindow.print()
            printWindow.close()
          }, 250)
        }
      } catch (error) {
        console.error('Error printing receipt:', error)
      }
    },
    async logout() {
      await authService.userLogout()
    },
    openCheckTillModal() {
      if (!this.canCompleteSale) {
        return;
      }
      this.c2bModal = true;
      this.c2bLoading = true;
      this.c2bTransactions = [];
      this.startC2BPolling();
    },
    closeC2bModal() {
      this.c2bModal = false;
      this.stopC2BPolling();
      this.c2bTransactions = [];
      this.c2bLoading = false;
    },
    async startC2BPolling() {
      await this.fetchC2BTransactions();

      this.c2bPollingInterval = setInterval(async () => {
        await this.fetchC2BTransactions();
      }, 2000);
    },
    stopC2BPolling() {
      if (this.c2bPollingInterval) {
        clearInterval(this.c2bPollingInterval);
        this.c2bPollingInterval = null;
      }
    },
    async fetchC2BTransactions() {
      try {
        const response = await storeMpesaService.getC2BTransactions(this.store.id);
        const transactions = Array.isArray(response) ? response : (response?.data || []);
        this.c2bTransactions = transactions;
        this.c2bLoading = false;

        if (transactions.length > 0) {
          this.stopC2BPolling();
        }
      } catch (error) {
        console.error('Error fetching C2B transactions:', error);
        this.c2bLoading = false;
      }
    },
    async assignC2BTransaction(transaction) {
      try {
        const response = await storeMpesaService.assignC2BToSale({
          transaction_id: transaction.id,
          reference: this.saleReference
        });
        if (response) {
          this.closeC2bModal();

          this.payment = {
            phone: transaction.msisdn,
            code: transaction.trans_id,
            amount: transaction.trans_amount
          };
          this.paymentModal = true;
          this.paymentSuccess = true;

          this.transaction.method = 'mpesa';
          this.transaction.payment_details = {
            phone: transaction.msisdn,
            code: transaction.trans_id,
            amount: transaction.trans_amount,
            timestamp: new Date().toISOString()
          };

          this.transaction.paid = parseFloat(transaction.trans_amount);
          this.transaction.balance = this.transaction.amount - this.transaction.paid;
          this.transaction.status = this.transaction.balance <= 0 ? 'complete' : 'pending';
        }
      } catch (error) {
        console.error('Error assigning C2B transaction:', error);
      }
    },
    generateUniqueOrderNo() {
      const now = new Date();
      const timestamp = now.getTime();
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      return `${timestamp}${random}`;
    },
    async initializePDQ() {
      if (this.pdqInitialized) {
        return true;
      }

      // notify.alert.info('Initializing PDQ device...');
      const result = await pdqService.initializePDQ(this.pdqBankId);
      
      if (result.success) {
        this.pdqInitialized = true;
        // this.pdqTerminalInfo = result.terminalInfo;
        return true;
      } else {
        notify.alert.error(`PDQ initialization failed at ${result.step}`);
        return false;
      }
    },
    async initiatePDQPayment() {
      if (!this.cartAvailable) {
        notify.alert.warning('Cart is empty');
        return;
      }

      this.pdqProcessing = true;

      const initialized = await this.initializePDQ();
      if (!initialized) {
        this.pdqProcessing = false;
        return;
      }

      const amount = Math.round(this.calculateTotal - this.discount);
      const orderNo = this.generateUniqueOrderNo();

      const initiatePayload = {
        order_no: orderNo,
        sale_reference: this.saleReference,
        amount: amount,
        store_id: this.store.id,
        initiated_at: new Date().toISOString()
      };

      const initiateResult = await saleService.initiatePDQ(initiatePayload);
      if (!initiateResult) {
        notify.alert.error('Failed to initiate PDQ payment');
        this.pdqProcessing = false;
        return;
      }

      notify.alert.info('Processing card payment...');
      const result = await pdqService.purchase(amount, orderNo);

      this.pdqProcessing = false;

      const resultsPayload = {
        order_no: orderNo,
        sale_reference: this.saleReference,
        amount: amount,
        success: result.success,
        status: result.status,
        transaction_data: result.transaction || null,
        error: result.error || null,
        completed_at: new Date().toISOString()
      };

      await saleService.resultsPDQ(resultsPayload);

      if (result.success && result.status === 'approved') {
        const txn = result.transaction;
        
        this.payment = {
          phone: txn.card_no || 'N/A',
          code: txn.voucher_no,
          amount: txn.trans_amount
        };
        this.paymentModal = true;
        this.paymentSuccess = true;

        this.transaction.method = 'pdq';
        this.transaction.payment_details = {
          order_no: txn.order_no,
          merchant_no: txn.merchant_no,
          terminal_no: txn.terminal_no,
          voucher_no: txn.voucher_no,
          batch_no: txn.batch_no,
          card_no: txn.card_no,
          card_type: txn.card_type,
          card_expire_date: txn.card_expire_date,
          trans_time: txn.trans_time,
          trans_amount: txn.trans_amount,
          ac: txn.ac,
          aid: txn.aid,
          tsi: txn.tsi,
          tvr: txn.tvr,
          cid: txn.cid,
          user: txn.user,
          timestamp: new Date().toISOString()
        };

        this.transaction.paid = parseFloat(txn.trans_amount);
        this.transaction.balance = this.transaction.amount - this.transaction.paid;
        this.transaction.status = this.transaction.balance <= 0 ? 'complete' : 'pending';
      } else if (result.status === 'duplicate') {
        notify.alert.warning('Duplicate order number detected, please retry');
      } else if (result.status === 'declined') {
        notify.alert.error('Card payment was declined');
      } else {
        notify.alert.error('Card payment failed');
      }
    },
    clearCart() {
      if (this.profile.role !== 'primary') {
        this.initiateCartOtpAuthorization({
          type: 'clear_cart',
          description: 'Clear all items from cart'
        });
        return;
      }
      this.doClearCart();
    },
    doClearCart() {
      sessionStorage.removeItem('cart')
      this.cartProducts = []
      this.cartQuantities = {}
      this.discount = 0
      this.transaction.paid = 0
      this.dataTableSearch = ''
      this.focusSearch()
    },
    async getSaleReference() {
      let reference = sessionStorage.getItem('saleReference')
      if (reference !== null) {
        this.saleReference = reference
      } else {
        let saleReference = await saleService.getSaleReference()
        this.saleReference = saleReference.reference
        sessionStorage.setItem('saleReference', saleReference.reference)
      }
    },
    async stkRequest() {
      let response = await saleService.stkRequest({
        store_id: this.store.id,
        reference: this.saleReference,
        phone: this.phone,
        amount: this.calculateTotal - this.discount
      })
      if (response.status === 200) {
        this.stkModal = false
        notify.alert.success('STK payment initiated successfully')

        setTimeout(() => {
          this.pollCompleteStk(8)
        }, 7000)
      }
    },
    async pollCompleteStk(retriesLeft) {
      if (retriesLeft <= 0) {
        notify.alert.warning('Payment verification timeout. Please check manually.', 3000)
        return
      }

      let response = await saleService.getCompleteStk({
        reference: this.saleReference
      })

      if (response) {
        this.payment = response
        this.paymentModal = true
        this.paymentSuccess = true

        this.transaction.method = 'mpesa'
        this.transaction.payment_details = {
          phone: response.phone,
          code: response.code,
          amount: response.amount,
          timestamp: new Date().toISOString()
        }

        this.transaction.paid = response.amount
        this.transaction.balance = this.transaction.amount - this.transaction.paid
        this.transaction.status = this.transaction.balance <= 0 ? 'complete' : 'pending'

        notify.alert.success('Payment confirmed!', 3000)
      } else {
        setTimeout(() => {
          this.pollCompleteStk(retriesLeft - 1)
        }, 2000)
      }
    },
    async getCompleteStk() {
      let response = await saleService.getCompleteStk({
        reference: this.saleReference
      })
      if (response) {
        this.payment = response
        this.paymentModal = true
        this.paymentSuccess = true

        // Update transaction with MPESA payment details
        this.transaction.method = 'mpesa'
        this.transaction.payment_details = {
          phone: response.phone,
          code: response.code,
          amount: response.amount,
          timestamp: new Date().toISOString()
        }

        // Update transaction status
        this.transaction.paid = response.amount
        this.transaction.balance = this.transaction.amount - this.transaction.paid
        this.transaction.status = this.transaction.balance <= 0 ? 'complete' : 'pending'
      } else {
        notify.alert.error('Payment not complete. Please try again.', 3000)
      }
    },
    focusSearch() {
      this.$refs.prodSearch.focus()
    },
    async refreshStockAndPrice() {
      try {
        this.isRefreshing = true
        const stockPriceUpdates = await posService.getStockAndPriceUpdates(this.store.id)
        if (stockPriceUpdates && stockPriceUpdates.length > 0) {
          const result = await posService.updateCachedStockAndPrice(this.store.id, stockPriceUpdates)
          if (result && result.hasNewProducts) {
            console.log('New products detected, fetching full product list...')
            this.dataTable = await posService.getPOSProducts(this.store.id, false)
          } else if (result && result.updated) {
            this.dataTable = result.updated
            console.log('Stock and price updated from backend')
          }
        }
      } catch (error) {
        console.error('Error refreshing stock and price:', error)
      } finally {
        this.isRefreshing = false
      }
    }
  },

  computed: {
    productData(){
      let data = [];
      for (let i = 0; i < this.dataTable.length; i++) {
        let fields = [
          this.dataTable[i].product_id,
          this.dataTable[i].product.sku,
          this.dataTable[i].product.code,
          this.dataTable[i].product.name,
          formatNumber(this.dataTable[i].price),
        ];
        data.push(fields);
      }
      return data;
    },
    calculateTotal() {
      let total = 0
      for (let i = 0; i < this.cartProducts.length; i++) {
        total += this.cartProducts[i].price * this.cartProducts[i].quantity
      }

      // Update transaction amount
      this.transaction.amount = total - this.discount

      // Calculate balance
      this.transaction.balance = this.transaction.amount - this.transaction.paid

      return total
    },
    calculateTax() {
      let tax = 0
      let total = 0
      for (let i = 0; i < this.cartProducts.length; i++) {
        total = this.cartProducts[i].price * this.cartProducts[i].quantity
        tax += this.cartProducts[i].product.tax_category.rate/100 * total
      }
      return tax
    },
    cartAvailable() {
      return this.cartProducts.length >= 1
    },
    canCompleteSale() {
      return this.cartAvailable && this.paymentSuccess
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    await this.checkActiveShift()
    this.getCartProducts()
    await this.getStoreProducts()
    await this.getSaleReference()
    await this.getLastSale()
    this.focusSearch()

    // Initialize transaction with store and reference
    this.transaction.store_id = this.store.id
    this.transaction.reference = this.saleReference
    this.paymentSuccess = this.transaction.method === 'cash';
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))

    let state = this
    $('.pos-component').on('click', function (e) {
      let type = $(e.target).get(0).tagName
      if (type === 'INPUT' || type === 'TEXTAREA') return
      if (state.$refs.prodSearch && !state.startShiftModal) {
        state.$refs.prodSearch.focus()
      }
    })
  },

  beforeUnmount() {
    this.stopOtpCountdown();
    this.stopC2BPolling();
  },

  watch: {
    dataTableSearch: function () {
      this.dataTableShow = this.dataTableSearch !== '';
    },
    'transaction.method': function () {
      // Update UI state based on payment method
      this.paymentSuccess = this.transaction.method === 'cash';
      this.stkModal = this.transaction.method === 'mpesa';

      // Reset payment details when method changes
      this.transaction.payment_details = {};

      // Update transaction status based on payment method
      if (this.transaction.method === 'cash') {
        // For cash, ensure paid amount is at least the total
        const finalTotal = this.calculateTotal - this.discount;
        if (!this.transaction.paid || this.transaction.paid < finalTotal) {
          this.transaction.paid = finalTotal;
        }
      } else {
        // For other methods, reset paid amount
        this.transaction.paid = 0;
      }

      // Recalculate balance
      this.transaction.balance = this.transaction.amount - this.transaction.paid;
      this.transaction.status = this.transaction.balance <= 0 ? 'complete' : 'pending';
    }
  }

}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* Base Styles */
.pos-component {
  font-family: 'Inter', sans-serif;
  color: #333;
}

/* Header Styles */
.header-container {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.nav-icon {
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-icon:hover {
  opacity: 0.7;
  transform: scale(1.05);
}

/* Card Styles */
.card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  transition: all 0.3s ease;
  overflow: hidden;
}

.card:hover {
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.card-header {
  border-bottom: 1px solid rgba(0,0,0,0.1);
  padding: 15px 20px;
}

/* Product Search and Table */
.product-search-container {
  position: relative;
}

.product-table {
  margin-top: 10px;
}

.product-cell {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.product-cell:hover {
  background-color: rgba(0,123,255,0.05);
}

/* Shopping Cart Styles */
.quantity-control {
  max-width: 120px;
}

.price-tag {
  color: #dc3545;
  font-weight: 600;
}

.price-tag-lg {
  color: #dc3545;
  font-weight: 700;
  font-size: 3em;
}

/* Payment Summary Styles */
.sale-badge {
  position: absolute;
  top: 0;
  right: 0;
  background-color: #dc3545;
  color: white;
  padding: 5px 15px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;
  transform: translateY(-50%);
  border-radius: 0 0 0 8px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}

.sale-reference {
  background-color: #f8f9fa;
  padding: 10px 15px;
  border-radius: 6px;
  border-left: 4px solid #007bff;
}

.payment-option {
  position: relative;
}

.payment-option input {
  position: absolute;
  opacity: 0;
}

.payment-option label {
  display: block;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.payment-option input:checked + label {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.summary-section {
  border-left: 4px solid #28a745;
}

/* Quick Action Buttons */
.quick-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 15px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 100%;
  border: 1px solid #dee2e6;
}

.quick-action-btn:hover {
  background-color: #e9ecef;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.quick-action-btn span {
  margin-top: 5px;
  font-size: 0.9rem;
  font-weight: 500;
}

/* C2B Modal Styles */
.rotating {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.transactions-list {
  max-height: 500px;
  overflow-y: auto;
}

/* Responsive Adjustments */
@media (max-width: 992px) {
  .quantity-control {
    max-width: 100px;
  }
}

@media (max-width: 768px) {
  .header-container h4 {
    font-size: 1.2rem;
  }

  .quick-action-btn {
    padding: 10px 5px;
  }

  .quick-action-btn span {
    font-size: 0.8rem;
  }
}
</style>
